/**
 * Connexion à la console.
 *
 * ★ **Aucun lien « créer un compte », aucun « mot de passe oublié ».** Ce n'est
 *   pas un oubli d'écran : un compte de console ouvre l'accès aux données de
 *   TOUS les clients. Il se crée en ligne de commande, une fois
 *   (`plateforme/console/amorcage.ts`), et se réinitialise de la même façon.
 *   Une réinitialisation par e-mail transformerait la boîte mail de
 *   l'exploitant en clé de la plateforme entière.
 *
 * ★ Le refus est **volontairement muet** : le backend renvoie le même message
 *   pour une adresse inconnue et pour un mot de passe faux. L'écran le
 *   retranscrit tel quel, sans essayer d'être plus serviable — distinguer les
 *   deux permettrait d'énumérer les comptes de console.
 */

import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router'

import { messageErreur } from '../../lib/api'
import { BandeauErreur, Button, Card, CardCorps, ChargementPage, FormField, Input } from '../../ui'
import { CHEMINS_CONSOLE } from './chemins'
import { useConnexion, useSessionConsole } from './api'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

export function ConnexionConsole() {
  const session = useSessionConsole()
  const connexion = useConnexion()
  const naviguer = useNavigate()
  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [secoursVisible, setSecoursVisible] = useState(false)
  const [code, setCode] = useState('')
  const methods = useQuery({ queryKey: ['console', 'auth-methods'],
    queryFn: () => api.get<{ keycloak: boolean }>('/console/auth/methods'), retry: false })
  const keycloak = methods.data?.keycloak === true

  if (session.data != null) return <Navigate to={CHEMINS_CONSOLE.racine} replace />
  if (methods.isLoading) return <ChargementPage />
  if (methods.isError) return <BandeauErreur message="Méthodes de connexion indisponibles." />

  async function soumettre(evenement: FormEvent): Promise<void> {
    evenement.preventDefault()
    setErreur(null)
    try {
      await connexion.mutateAsync({ email, motDePasse, ...(keycloak ? { code } : {}) })
      await naviguer(CHEMINS_CONSOLE.racine, { replace: true })
    } catch (cause) {
      setErreur(messageErreur(cause, 'Connexion impossible.'))
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="text-lg font-semibold text-white">Console d’exploitation</p>
          <p className="mt-1 text-sm text-slate-400">
            Accès réservé à l’éditeur. Chaque geste est journalisé.
          </p>
        </div>

        <Card>
          <CardCorps>
            {keycloak && (
              <div className="mb-5 space-y-3">
                <a className="block rounded-md bg-blue-600 px-4 py-2 text-center font-semibold text-white hover:bg-blue-500"
                  href="/api/console/auth/oidc/start">Se connecter avec Keycloak</a>
                <button type="button" className="text-sm text-slate-600 underline"
                  onClick={() => { setSecoursVisible((visible) => !visible) }}>
                  {secoursVisible ? 'Masquer l’accès de secours' : 'Afficher identifiant et mot de passe de secours'}
                </button>
              </div>
            )}
            {(!keycloak || secoursVisible) && (
            <form
              className="space-y-4"
              onSubmit={(evenement) => {
                void soumettre(evenement)
              }}
            >
              {erreur !== null && <BandeauErreur message={erreur} />}

              <FormField label="Adresse e-mail" requis>
                {(champ) => (
                  <Input
                    {...champ}
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                    }}
                  />
                )}
              </FormField>

              {keycloak && <FormField label="Code MFA local" requis>
                {(champ) => <Input {...champ} inputMode="numeric" autoComplete="one-time-code"
                  value={code} onChange={(e) => { setCode(e.target.value) }} />}
              </FormField>}

              <FormField label="Mot de passe" requis>
                {(champ) => (
                  <Input
                    {...champ}
                    type="password"
                    autoComplete="current-password"
                    value={motDePasse}
                    onChange={(e) => {
                      setMotDePasse(e.target.value)
                    }}
                  />
                )}
              </FormField>

              <Button type="submit" pleineLargeur chargement={connexion.isPending}>
                Entrer
              </Button>
            </form>
            )}
          </CardCorps>
        </Card>

        {!keycloak && <p className="mt-4 text-center text-xs text-slate-500">
          Un compte de console se crée en ligne de commande, jamais depuis cet écran.
        </p>}
      </div>
    </div>
  )
}
